/**
 * Restriction Wrapper.
 *
 * Restricts access to children based on authentication status and optional storage role.
 */

import { FC, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { AccessRole, AuthenticationStatus, FormStatus } from "types"
import useAuth from "hooks/useAuth"
import useAccess from "hooks/useAccess"
import useTargetStorage from "hooks/useTargetStorage"

interface RestrictionWrapperProps {
    children: React.ReactNode;
    restrictedTo?: AuthenticationStatus;
    requiredRole?: AccessRole[];
    storageId?: string;
    fallbackURL?: string;
}

export const OWNER_ROLES: AccessRole[] = [AccessRole.OWNER]
export const EDITOR_ROLES: AccessRole[] = [AccessRole.OWNER, AccessRole.EDITOR]
export const WORKER_ROLES: AccessRole[] = [AccessRole.OWNER, AccessRole.EDITOR, AccessRole.WORKER]

const RestrictionWrapper: FC<RestrictionWrapperProps> = ({ children, restrictedTo, requiredRole, storageId, fallbackURL }) => {
    const { enforceRestriction, accountPublicData, tokenIsValid, tokenValidationStatus, getUserId } = useAuth();
    const { targetStorageDocId } = useTargetStorage();
    const navigate = useNavigate();
    const resolvedStorageId = storageId ?? targetStorageDocId;
    const { getUserRole, storageInvitationsStatus } = useAccess({
        storageId: requiredRole ? resolvedStorageId : undefined,
    });

    const waitingForAuth = tokenValidationStatus === FormStatus.LOADING || (restrictedTo === AuthenticationStatus.online && !tokenIsValid);
    const waitingForRole = !!(requiredRole && (
        waitingForAuth ||
        !getUserId() ||
        storageInvitationsStatus === FormStatus.LOADING
    ));

    useEffect(() => {
        if (restrictedTo) {
            enforceRestriction(restrictedTo, fallbackURL);
        }
    }, [enforceRestriction, fallbackURL, restrictedTo, accountPublicData]);

    useEffect(() => {
        if (!requiredRole || waitingForRole) return;
        if (!resolvedStorageId) {
            navigate('/storages');
            return;
        }
        if (!getUserRole || !requiredRole.includes(getUserRole)) {
            navigate('/401');
        }
    }, [requiredRole, waitingForRole, resolvedStorageId, getUserRole, navigate]);

    if (restrictedTo && enforceRestriction(restrictedTo)) {
        return null;
    }
    if (requiredRole) {
        if (waitingForRole) {
            return null;
        }
        if (!resolvedStorageId || !getUserRole || !requiredRole.includes(getUserRole)) {
            return null;
        }
    }
    return <>{children}</>;
}

export default RestrictionWrapper
